import { useEffect, useMemo, useRef } from 'react'

/*
 * Background for the sign-in and register pages, back to front:
 *
 *   1. Deep layer: a faint grid and a strip of candlesticks drifting
 *      sideways. The candles are a fixed repeating image; only the strip
 *      moves, via a compositor-only transform.
 *   2. "Matrix rain" of crypto tickers and symbols on a transparent <canvas>.
 *      One rAF loop, throttled to a fixed step rate: each step erases a little
 *      of the canvas (the fading trail) and every active column paints one new
 *      cell. Each drop gets a depth tier, so nearer drops are slightly larger,
 *      brighter and faster than farther ones.
 *   3. A vignette that darkens the edges and the area behind the card.
 *
 * Phones get fewer, larger columns and a slower step; reduced-motion users
 * get a single static frame and still candles.
 */

const TICKERS = ['BTC', 'ETH', 'SOL', 'ADA', 'XRP', 'DOGE', 'LINK', 'AVAX', 'DOT']
const UP = '▲'
const DOWN = '▼'

const GREENS = ['#15803d', '#16a34a', '#22c55e', '#22c55e', '#4ade80']
const HEAD = '#bbf7d0'
const ACCENTS = ['#60a5fa', '#fbbf24', '#a78bfa', '#f472b6']
const UP_COLOR = '#4ade80'
const DOWN_COLOR = '#f87171'

// Kept low so the rain reads as texture rather than content.
const BODY_ALPHA = 0.5
const HEAD_ALPHA = 0.85
// Share of the canvas erased every other step; sets the trail length. Erasing
// ~9% every step would be equivalent, but 8-bit alpha rounds 9% of a faint
// pixel to nothing, so faint ghosts would pile up; 18% fades them out fully.
const FADE_ALPHA = 0.18

interface Tier {
  scale: number
  alpha: number
  speed: number
}

// Deliberately close together: a hint of depth, not a dramatic parallax.
const TIERS: readonly Tier[] = [
  { scale: 0.85, alpha: 0.5, speed: 0.5 }, // far
  { scale: 1, alpha: 0.82, speed: 0.75 }, // mid
  { scale: 1.15, alpha: 1, speed: 1 }, // near
]

interface Cell {
  text: string
  color: string
  bright: boolean
}

interface Column {
  tier: number
  row: number
  progress: number
  delay: number
  prev: Cell | null
}

const pick = <T,>(items: readonly T[]): T => items[(Math.random() * items.length) | 0]

// Roughly: 28% tickers, 8% ₿/Ξ, 10% $/%, 8% arrows, the rest digits.
const nextCell = (): Cell => {
  const r = Math.random()
  let text: string
  if (r < 0.28) text = pick(TICKERS)
  else if (r < 0.36) text = Math.random() < 0.55 ? '₿' : 'Ξ'
  else if (r < 0.46) text = Math.random() < 0.6 ? '$' : '%'
  else if (r < 0.54) text = Math.random() < 0.55 ? UP : DOWN
  else text = String((Math.random() * 10) | 0)

  if (text === UP) return { text, color: UP_COLOR, bright: true }
  if (text === DOWN) return { text, color: DOWN_COLOR, bright: true }
  if (Math.random() < 0.08) return { text, color: pick(ACCENTS), bright: true }
  return { text, color: pick(GREENS), bright: false }
}

// 40% far, 40% mid, 20% near: most of the rain sits back.
const pickTier = () => {
  const r = Math.random()
  return r < 0.4 ? 0 : r < 0.8 ? 1 : 2
}

const newColumn = (maxDelay: number): Column => ({
  tier: pickTier(),
  row: 0,
  progress: 0,
  delay: (Math.random() * maxDelay) | 0,
  prev: null,
})

// One tile of candlesticks, repeated side by side. The tile's price walk
// closes where it opened, so the tiles join without a jump and the strip can
// loop by sliding exactly one tile width (see .auth-candles in index.css).
const CANDLE_COUNT = 48
const CANDLE_STEP = 15 // px per candle; the tile is drawn 1:1 horizontally
const CANDLE_BODY = 9
const CANDLE_TILE_W = CANDLE_COUNT * CANDLE_STEP // 720, mirrored in CSS
const CANDLE_TILE_H = 200 // stretched to the strip's height

const candleTile = () => {
  // Noise plus one slow swing so it reads as a market, not static.
  const moves = Array.from(
    { length: CANDLE_COUNT },
    (_, i) =>
      (Math.random() - 0.5) * 2 * (0.5 + Math.random()) +
      Math.sin((i / CANDLE_COUNT) * Math.PI * 2) * 0.6
  )
  const drift = moves.reduce((a, b) => a + b, 0) / CANDLE_COUNT
  let price = 0
  const candles = moves.map((move) => {
    const open = price
    const close = open + move - drift
    price = close
    const wick = 0.2 + Math.random() * 0.6
    return {
      open,
      close,
      high: Math.max(open, close) + Math.random() * wick,
      low: Math.min(open, close) - Math.random() * wick,
    }
  })

  const top = Math.max(...candles.map((c) => c.high))
  const bottom = Math.min(...candles.map((c) => c.low))
  const pad = 12
  const y = (v: number) => pad + ((top - v) / (top - bottom)) * (CANDLE_TILE_H - pad * 2)
  const r = (n: number) => n.toFixed(1)

  const up: string[] = []
  const down: string[] = []
  candles.forEach((c, i) => {
    const x = i * CANDLE_STEP
    const bodyTop = y(Math.max(c.open, c.close))
    const bodyH = Math.max(1.5, y(Math.min(c.open, c.close)) - bodyTop)
    const shapes = c.close >= c.open ? up : down
    shapes.push(
      `<rect x='${x + CANDLE_STEP / 2 - 0.5}' y='${r(y(c.high))}' width='1' height='${r(y(c.low) - y(c.high))}'/>`,
      `<rect x='${x + (CANDLE_STEP - CANDLE_BODY) / 2}' y='${r(bodyTop)}' width='${CANDLE_BODY}' height='${r(bodyH)}' rx='1'/>`
    )
  })

  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${CANDLE_TILE_W} ${CANDLE_TILE_H}' preserveAspectRatio='none'>` +
    `<g fill='#22c55e'>${up.join('')}</g><g fill='#ef4444'>${down.join('')}</g></svg>`
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}

const DeepLayer = () => {
  const tile = useMemo(candleTile, [])
  return (
    <>
      <div className="auth-grid absolute inset-0" />
      {/* On phones the card covers the middle, so the strip sits below it. */}
      <div className="absolute inset-x-0 bottom-0 h-[18vh] overflow-hidden opacity-[0.14] sm:bottom-auto sm:top-[36%] sm:h-[32vh]">
        <div
          className="auth-candles h-full"
          style={{ backgroundImage: tile, backgroundSize: `${CANDLE_TILE_W}px 100%` }}
        />
      </div>
    </>
  )
}

const CryptoRain = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (!canvas || !ctx) return

    const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
    const cores = navigator.hardwareConcurrency || 8

    let width = 0
    let height = 0
    let colW = 44
    let stepMs = 55
    let restartDelay = 40
    // Per tier: font, row height, and how many rows fit on screen.
    let fonts: string[] = []
    let rowHs: number[] = []
    let rowCounts: number[] = []
    let currentFont = ''
    let columns: Column[] = []
    let raf = 0
    let last = 0
    let stepCount = 0

    const paint = (cell: Cell, tier: Tier, x: number, y: number, head: boolean) => {
      if (head && !cell.bright) {
        ctx.globalAlpha = HEAD_ALPHA * tier.alpha
        ctx.fillStyle = HEAD
      } else {
        ctx.globalAlpha = (cell.bright ? HEAD_ALPHA : BODY_ALPHA) * tier.alpha
        ctx.fillStyle = cell.color
      }
      ctx.fillText(cell.text, x, y)
    }

    const step = () => {
      // Erase rather than paint over, so the deep layer shows through.
      if (++stepCount % 2 === 0) {
        ctx.globalCompositeOperation = 'destination-out'
        ctx.globalAlpha = FADE_ALPHA
        ctx.fillStyle = '#000'
        ctx.fillRect(0, 0, width, height)
        ctx.globalCompositeOperation = 'source-over'
      }

      for (let i = 0; i < columns.length; i++) {
        const col = columns[i]
        if (col.delay > 0) {
          col.delay--
          continue
        }
        const tier = TIERS[col.tier]
        col.progress += tier.speed
        if (col.progress < 1) continue
        col.progress -= 1

        // Font parsing isn't free; only switch when the tier changes.
        if (currentFont !== fonts[col.tier]) {
          currentFont = fonts[col.tier]
          ctx.font = currentFont
        }
        const rowH = rowHs[col.tier]
        const x = i * colW + colW / 2

        // The last head drops back to its body colour before the new head lands.
        if (col.prev && col.row > 0) {
          const py = (col.row - 1) * rowH
          ctx.clearRect(i * colW, py, colW, rowH)
          paint(col.prev, tier, x, py, false)
        }

        const cell = nextCell()
        paint(cell, tier, x, col.row * rowH, true)
        col.prev = cell
        col.row++

        const rows = rowCounts[col.tier]
        if (col.row > rows || (col.row > rows * 0.5 && Math.random() < 0.015)) {
          columns[i] = newColumn(restartDelay)
        }
      }
    }

    const setup = () => {
      width = window.innerWidth
      height = Math.max(window.innerHeight, height)
      const lite = width < 768 || cores <= 4
      const dpr = Math.min(window.devicePixelRatio || 1, lite ? 1 : 1.5)

      const fontSize = width < 480 ? 12 : 14
      // Wide enough for a 4-letter ticker at the nearest tier's size.
      colW = Math.round(fontSize * 3.2)
      fonts = TIERS.map(
        (t) =>
          `${Math.round(fontSize * t.scale)}px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace`
      )
      rowHs = TIERS.map((t) => Math.round(fontSize * t.scale * 1.4))
      rowCounts = rowHs.map((h) => Math.ceil(height / h))
      const rows = rowCounts[1]
      stepMs = lite ? 80 : 55
      // Longer idle gaps between drops on phones keep the rain sparse.
      restartDelay = lite ? Math.round(rows * 0.6) : 40

      canvas.width = Math.floor(width * dpr)
      canvas.height = Math.floor(height * dpr)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      ctx.textAlign = 'center'
      ctx.textBaseline = 'top'
      currentFont = ''

      const count = Math.ceil(width / colW)
      columns = Array.from({ length: count }, () => newColumn(rows))

      // Pre-roll so the page opens mid-rain instead of on an empty screen.
      for (let s = 0; s < rows; s++) step()
    }

    const loop = (now: number) => {
      raf = requestAnimationFrame(loop)
      if (now - last < stepMs) return
      last = now
      step()
    }

    setup()
    if (!reducedMotion) raf = requestAnimationFrame(loop)

    // Mobile browsers fire resize as the URL bar slides; only rebuild when the
    // width changes or the viewport grows past the canvas.
    let resizeTimer: ReturnType<typeof setTimeout> | undefined
    const onResize = () => {
      clearTimeout(resizeTimer)
      resizeTimer = setTimeout(() => {
        if (window.innerWidth !== width || window.innerHeight > height) setup()
      }, 150)
    }
    window.addEventListener('resize', onResize)

    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(resizeTimer)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 overflow-hidden bg-[#050a08]"
    >
      <DeepLayer />
      <canvas ref={canvasRef} className="absolute left-0 top-0" />
      {/* Darken the middle behind the card and the edges for a vignette. */}
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(5,10,8,0.55)_0%,rgba(5,10,8,0.15)_55%,rgba(5,10,8,0.7)_100%)]" />
    </div>
  )
}

export default CryptoRain
